import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { addDays, endOfMonth, startOfDay, startOfMonth } from "date-fns";
import { convertCurrencyAmount } from "@/lib/fx-rates";

export interface MembershipDashboardStats {
  activeMembers: number;
  expiringSoon: number; // next 7 days
  visitsToday: number; // total people count (party_size)
  memberVisitsToday: number;
  walkInsToday: number;
  walkInRevenueToday: number;
  membershipRevenueThisMonth: number;
  newMembersThisMonth: number;
  recentVisits: Array<{
    id: string;
    check_in_at: string;
    check_out_at: string | null;
    party_size: number;
    customer_name: string;
    kind: "member" | "walk_in";
    label: string; // plan name or day pass code
  }>;
}

export function useMembershipDashboardStats() {
  const { business } = useBusiness();
  const sb = supabase as any;

  const [stats, setStats] = useState<MembershipDashboardStats>({
    activeMembers: 0,
    expiringSoon: 0,
    visitsToday: 0,
    memberVisitsToday: 0,
    walkInsToday: 0,
    walkInRevenueToday: 0,
    membershipRevenueThisMonth: 0,
    newMembersThisMonth: 0,
    recentVisits: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!business?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const today = startOfDay(now);
      const tomorrow = addDays(today, 1);
      const expSoonEnd = addDays(today, 7);
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const displayCurrency = business.currency || "GHS";
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || displayCurrency, displayCurrency);

      // 1) Membership counts
      const { data: memberships, error: mErr } = await sb
        .from("memberships")
        .select("id,status,expires_at,created_at")
        .eq("business_id", business.id);
      if (mErr) throw mErr;

      const activeMembers = (memberships || []).filter((m: any) => m.status === "active").length;
      const expiringSoon = (memberships || []).filter((m: any) => {
        if (m.status !== "active") return false;
        const d = new Date(String(m.expires_at));
        return d >= today && d < expSoonEnd;
      }).length;
      const newMembersThisMonth = (memberships || []).filter((m: any) => {
        const d = new Date(String(m.created_at));
        return d >= monthStart && d <= monthEnd;
      }).length;

      // 2) Visits today (access_sessions)
      const { data: sessions, error: sErr } = await sb
        .from("access_sessions")
        .select(
          "id,check_in_at,check_out_at,party_size,customer:customers(name),membership:memberships(id,membership_code,plan:membership_plans(name)),day_pass:day_passes(ticket_code)",
        )
        .eq("business_id", business.id)
        .gte("check_in_at", today.toISOString())
        .lt("check_in_at", tomorrow.toISOString())
        .order("check_in_at", { ascending: false })
        .limit(12);
      if (sErr) throw sErr;

      const memberVisitsToday = (sessions || [])
        .filter((s: any) => !!s.membership)
        .reduce((sum: number, s: any) => sum + Number(s.party_size || 1), 0);
      const walkInsToday = (sessions || [])
        .filter((s: any) => !!s.day_pass)
        .reduce((sum: number, s: any) => sum + Number(s.party_size || 1), 0);
      const visitsToday = memberVisitsToday + walkInsToday;

      const recentVisits = (sessions || []).map((s: any) => {
        const isMember = !!s.membership;
        const planName = s.membership?.plan?.name as string | undefined;
        const memberCode = s.membership?.membership_code as string | undefined;
        const dayPassCode = s.day_pass?.ticket_code as string | undefined;
        return {
          id: s.id,
          check_in_at: s.check_in_at,
          check_out_at: s.check_out_at ?? null,
          party_size: Number(s.party_size || 1),
          customer_name: s.customer?.name || "Unknown",
          kind: isMember ? "member" : "walk_in",
          label: isMember ? planName || memberCode || "Member" : dayPassCode || "Walk-in",
        };
      });

      // 3) Walk-in revenue today (access_payments)
      const { data: payments, error: pErr } = await sb
        .from("access_payments")
        .select("amount,currency,status,day_pass_id,created_at")
        .eq("business_id", business.id)
        .gte("created_at", today.toISOString())
        .lt("created_at", tomorrow.toISOString());
      if (pErr) throw pErr;

      const walkInRevenueToday = (payments || [])
        .filter((p: any) => !!p.day_pass_id && (p.status === "paid" || p.status == null))
        .reduce((sum: number, p: any) => sum + convertRevenue(Number(p.amount || 0), p.currency), 0);

      // 4) Membership revenue this month (successful renewal payments)
      const { data: membershipPayments, error: mpErr } = await sb
        .from("platform_payments")
        .select("gross_amount, currency, paid_at")
        .eq("business_id", business.id)
        .eq("context", "membership_payment")
        .eq("status", "success")
        .gte("paid_at", monthStart.toISOString())
        .lte("paid_at", monthEnd.toISOString());
      if (mpErr) throw mpErr;

      const membershipRevenueThisMonth = (membershipPayments || []).reduce(
        (sum: number, p: any) => sum + convertRevenue(Number(p.gross_amount || 0), p.currency),
        0,
      );

      setStats({
        activeMembers,
        expiringSoon,
        visitsToday,
        memberVisitsToday,
        walkInsToday,
        walkInRevenueToday,
        membershipRevenueThisMonth,
        newMembersThisMonth,
        recentVisits,
      });
    } catch (e: any) {
      setError(e?.message || "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  }, [business?.currency, business?.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}


